import { createFileRoute, Link } from '@tanstack/react-router';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';
import { useRouteMeta } from '@/lib/use-route-meta';

export const Route = createFileRoute('/solucao')({ component: SolucaoPage });

const FOR_CLIENTS = [
  { icon: '🔍', title: 'Encontre o profissional certo', desc: 'Perfis detalhados com especialidades, abordagens, preços e avaliações reais de outros clientes.' },
  { icon: '📅', title: 'Agende em minutos', desc: 'Veja a disponibilidade em tempo real e confirme o horário com um clique, sem ligações ou e-mails.' },
  { icon: '💳', title: 'Pague com segurança', desc: 'PIX ou cartão de crédito. Pagamento processado de forma segura antes da sessão.' },
  { icon: '🎥', title: 'Online ou presencial', desc: 'Sessões online via Google Meet integrado ou presencialmente no consultório do profissional.' },
];

const FOR_THERAPISTS = [
  { icon: '🗓️', title: 'Agenda organizada', desc: 'Defina seus horários disponíveis e deixe o sistema cuidar dos agendamentos automaticamente.' },
  { icon: '💰', title: 'Recebimentos automáticos', desc: 'Pagamentos confirmados antes da sessão. Repasses organizados em ciclos mensais, sem burocracia.' },
  { icon: '📋', title: 'Prontuário digital', desc: 'Registre evoluções de sessão com segurança. Acessível apenas por você.' },
  { icon: '📊', title: 'Visibilidade profissional', desc: 'Perfil público com avaliações, especialidades e modalidades de atendimento.' },
];

const DIFFERENTIALS = [
  { title: 'Sem mensalidade', desc: 'Você paga apenas quando realiza sessões. Sem contratos ou taxas fixas.', tone: 'teal' as const },
  { title: 'Transparência total', desc: 'Valores claros, sem taxas escondidas. O profissional sabe exatamente o que vai receber.', tone: 'coral' as const },
  { title: 'Privacidade e LGPD', desc: 'Dados de saúde protegidos com criptografia e em conformidade com a legislação brasileira.', tone: 'cream' as const },
];

function SolucaoPage() {
  useRouteMeta({
    title: 'A solução Nosso Ello — terapia online e presencial sem burocracia',
    description: 'Para clientes: perfis verificados, agenda em tempo real, pagamento seguro. Para terapeutas: gestão de agenda, repasse automático e prontuário digital. Tudo em um só lugar.',
    canonical: 'https://nossoello.com.br/solucao',
  });
  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        <section className="max-w-4xl mx-auto px-6 pt-16 pb-14 text-center">
          <div className="inline-flex items-center gap-2 bg-coral-50 border border-coral-100 rounded-full px-4 py-1.5 text-coral-600 text-sm font-medium mb-6">
            Para clientes e profissionais
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-800 leading-tight tracking-tight">
            Uma plataforma para os dois lados do cuidado
          </h1>
          <p className="text-lg text-slate-500 mt-4 max-w-2xl mx-auto leading-relaxed">
            A Nosso Ello conecta quem busca cuidado emocional com profissionais qualificados — de forma simples, segura e humana.
          </p>
        </section>

        <section className="max-w-5xl mx-auto px-6 pb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="size-9 rounded-full bg-teal-400 flex items-center justify-center text-white font-bold text-sm shrink-0">P</div>
            <h2 className="text-2xl font-extrabold text-slate-800">Para clientes</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {FOR_CLIENTS.map((item) => <FeatureItem key={item.title} {...item} />)}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 pb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="size-9 rounded-full bg-coral-400 flex items-center justify-center text-white font-bold text-sm shrink-0">T</div>
            <h2 className="text-2xl font-extrabold text-slate-800">Para profissionais</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {FOR_THERAPISTS.map((item) => <FeatureItem key={item.title} {...item} />)}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 pb-20">
          <h2 className="text-2xl font-extrabold text-slate-800 mb-6 text-center">Por que a Nosso Ello?</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {DIFFERENTIALS.map((d) => <DifferentialCard key={d.title} {...d} />)}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 pb-20">
          <div className="bg-gradient-to-r from-teal-400 to-coral-400 rounded-[28px] p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3">Comece hoje mesmo</h2>
            <p className="text-white/90 mb-8 max-w-lg mx-auto">
              Cliente ou profissional — a Nosso Ello tem um espaço para você.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/register" className="bg-white text-teal-700 hover:bg-teal-50 px-8 py-3 rounded-full font-semibold transition shadow-sm">
                Criar conta grátis
              </Link>
              <Link to="/como-funciona" className="bg-white/20 border border-white/40 hover:bg-white/30 text-white px-8 py-3 rounded-full font-medium transition">
                Como funciona →
              </Link>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

function FeatureItem({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm flex gap-4 items-start">
      <span className="text-2xl shrink-0">{icon}</span>
      <div>
        <h3 className="font-semibold text-slate-800 mb-1">{title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function DifferentialCard({ title, desc, tone }: { title: string; desc: string; tone: 'teal' | 'coral' | 'cream' }) {
  const styles = {
    teal: { bg: 'bg-teal-300', text: 'text-white', sub: 'text-white/90' },
    coral: { bg: 'bg-coral-300', text: 'text-white', sub: 'text-white/90' },
    cream: { bg: 'bg-coral-100', text: 'text-slate-700', sub: 'text-slate-600' },
  }[tone];
  return (
    <div className={`${styles.bg} rounded-[28px] p-7 shadow-sm`}>
      <h3 className={`text-lg font-bold ${styles.text} mb-2`}>{title}</h3>
      <p className={`text-sm ${styles.sub} leading-relaxed`}>{desc}</p>
    </div>
  );
}
